import type { AgentToolDefinition } from '../../../core/api/generated/models/AgentToolDefinition';
import type { ChannelAccount } from '../../../core/api/generated/models/ChannelAccount';
import type { Integration } from '../../../core/api/generated/models/Integration';
import type { KnowledgeBase } from '../../../core/api/generated/models/KnowledgeBase';
import type {
  BuilderToolFieldKind,
  BuilderToolFieldOption,
  BuilderToolFieldValue,
  BuilderToolFieldView,
  BuilderToolPolicyDraft,
} from './agent-builder.types';

export interface ToolFieldCatalogs {
  readonly channelAccounts: readonly ChannelAccount[];
  readonly selectedChannelAccountIds: readonly string[];
  readonly integrations: readonly Integration[];
  readonly selectedIntegrationIds: readonly string[];
  readonly knowledgeBases: readonly KnowledgeBase[];
  /** Business-hours calendars (for the `business_hours_calendars` bind source). */
  readonly businessHoursCalendars: readonly { readonly id: string; readonly name: string }[];
  /** Agent-level sub-account bindings, keyed by `${optionsFrom}:${kind}`. */
  readonly agentSubAccountBindings: Record<string, string>;
}

export function emptyToolPolicyDraft(): BuilderToolPolicyDraft {
  return {
    defaults: {},
    fixedArguments: {},
    fieldGuidance: {},
    enabledOptionalFields: [],
    hiddenOptionalFields: [],
  };
}

export function normalizeToolPolicyDraft(value: unknown): BuilderToolPolicyDraft {
  const record = asRecord(value);
  return {
    defaults: asRecord(record['defaults']),
    fixedArguments: asRecord(record['fixedArguments']),
    fieldGuidance: stringRecord(record['fieldGuidance']),
    enabledOptionalFields: stringArray(record['enabledOptionalFields']),
    hiddenOptionalFields: stringArray(record['hiddenOptionalFields']),
  };
}

export function buildToolFieldViews(
  tool: AgentToolDefinition,
  policy: BuilderToolPolicyDraft | undefined,
  catalogs: ToolFieldCatalogs,
): BuilderToolFieldView[] {
  const normalizedPolicy = policy ?? emptyToolPolicyDraft();
  const schema = asRecord(tool.inputSchema);
  const properties = asRecord(schema['properties']);
  const required = new Set(stringArray(schema['required']));
  const policyDefaults = asRecord(normalizedPolicy.defaults);
  const policyFixedArguments = asRecord(normalizedPolicy.fixedArguments);
  const businessPolicy = integrationToolPolicyDraft(tool, catalogs);
  const businessDefaults = asRecord(businessPolicy.defaults);
  const businessFixedArguments = asRecord(businessPolicy.fixedArguments);
  const definitionDefaults = asRecord(asRecord(tool.defaultPayloadPolicy)['defaults']);

  return Object.entries(properties).map(([key, rawProperty]) => {
    const property = asRecord(rawProperty);
    const options = optionsForField(key, property, catalogs);
    const kind = fieldKind(property, options);
    const isRequired = required.has(key);
    const locked = hasOwn(policyFixedArguments, key) || hasOwn(businessFixedArguments, key);
    const agentValue = agentBindingValueForField(property, catalogs);
    const hasLocalOverride =
      hasOwn(policyFixedArguments, key) ||
      hasOwn(businessFixedArguments, key) ||
      hasOwn(policyDefaults, key) ||
      hasOwn(businessDefaults, key);
    // Agent-level binding is inherited unless this tool overrides it locally.
    const inheritedFromAgent = agentValue !== null && !hasLocalOverride;
    const value = readFieldValue(
      policyFixedArguments[key] ??
        businessFixedArguments[key] ??
        policyDefaults[key] ??
        businessDefaults[key] ??
        agentValue ??
        definitionDefaults[key] ??
        property['default'] ??
        null,
      kind,
    );
    const guidance = normalizedPolicy.fieldGuidance[key] ?? businessPolicy.fieldGuidance[key] ?? '';
    const enabledByPolicy = normalizedPolicy.enabledOptionalFields.includes(key);
    const hiddenByPolicy = normalizedPolicy.hiddenOptionalFields.includes(key);
    const enabledByBusiness = businessPolicy.enabledOptionalFields.includes(key);
    const hiddenByBusiness = businessPolicy.hiddenOptionalFields.includes(key);
    const hidden = hiddenByPolicy || (hiddenByBusiness && !enabledByPolicy);
    const hasUserConfig = hasMeaningfulValue(value) || Boolean(guidance);
    const enabled = isRequired
      || enabledByPolicy
      || (!hidden && (enabledByBusiness || hasUserConfig));
    return {
      key,
      label: labelForField(key, property),
      description: stringValue(property['description']),
      required: isRequired,
      enabled,
      kind,
      value,
      guidance,
      locked,
      options,
      inheritedFromAgent,
    };
  });
}

export function compactToolPolicyDraft(
  tool: AgentToolDefinition,
  fields: readonly BuilderToolFieldView[],
): BuilderToolPolicyDraft {
  const defaults: Record<string, unknown> = {};
  const fixedArguments: Record<string, unknown> = {};
  const fieldGuidance: Record<string, string> = {};
  const enabledOptionalFields: string[] = [];
  const hiddenOptionalFields: string[] = [];

  for (const field of fields) {
    if (!field.required) {
      if (field.enabled) enabledOptionalFields.push(field.key);
      else hiddenOptionalFields.push(field.key);
    }
    if (field.enabled && hasMeaningfulValue(field.value)) {
      if (field.locked) fixedArguments[field.key] = serializedFieldValue(field);
      else defaults[field.key] = serializedFieldValue(field);
    }
    if (field.enabled && field.guidance.trim()) {
      fieldGuidance[field.key] = field.guidance.trim();
    }
  }

  return {
    defaults,
    fixedArguments,
    fieldGuidance,
    enabledOptionalFields,
    hiddenOptionalFields,
  };
}

function serializedFieldValue(field: BuilderToolFieldView): unknown {
  if (field.kind !== 'json' || typeof field.value !== 'string') return field.value;
  try {
    return JSON.parse(field.value) as unknown;
  } catch {
    return field.value;
  }
}

export function optionalToolFields(fields: readonly BuilderToolFieldView[]): BuilderToolFieldView[] {
  return fields.filter((field) => !field.required && !field.enabled);
}

export function coerceFieldValue(value: unknown, kind: BuilderToolFieldKind): BuilderToolFieldValue {
  return readFieldValue(value, kind);
}

function fieldKind(property: Record<string, unknown>, options: readonly BuilderToolFieldOption[]): BuilderToolFieldKind {
  const type = schemaType(property);
  if (type === 'array') return options.length ? 'multi_select' : 'json';
  if (options.length) return 'select';
  if (type === 'number' || type === 'integer') return 'number';
  if (type === 'boolean') return 'boolean';
  if (type === 'object') return 'json';
  if (stringValue(property['format']) === 'textarea') return 'textarea';
  return 'text';
}

function optionsForField(
  key: string,
  property: Record<string, unknown>,
  catalogs: ToolFieldCatalogs,
): BuilderToolFieldOption[] {
  const schemaOptions = schemaEnumOptions(property);
  if (schemaOptions.length) return schemaOptions;

  // Generic, neutral binding: a tool def marks a sub-account-scoped field (agenda,
  // WhatsApp number, …) with `bindTimeConfigurableField`; we render a picker from
  // the named catalog. Value is the provider-scoped external id the tool expects.
  const markerOptions = optionsFromBindMarker(property, catalogs);
  if (markerOptions) return markerOptions;

  const normalizedKey = key.toLocaleLowerCase('pt-BR').replace(/[_-]/g, '');
  if (normalizedKey.includes('channelaccount')) {
    return catalogs.channelAccounts.map((account) => ({
      value: account.id,
      label: account.label || account.externalSenderId || account.externalChannelId || account.id,
      description: account.kind,
    }));
  }
  if (normalizedKey.includes('integration')) {
    return catalogs.integrations.map((integration) => ({
      value: integration.id,
      label: integration.displayName,
      description: integration.provider,
    }));
  }
  if (normalizedKey.includes('knowledgebase') || normalizedKey.includes('library')) {
    return catalogs.knowledgeBases.map((base) => ({
      value: base.id,
      label: base.name,
      description: base.type,
    }));
  }
  return [];
}

/** The `channel_accounts` catalog source, filtered by an optional channel kind. */
const BIND_SOURCE_CHANNEL_ACCOUNTS = 'channel_accounts';
const BIND_SOURCE_BUSINESS_HOURS = 'business_hours_calendars';

/** A tool field marked as agent/sub-account-configurable via `bindTimeConfigurableField`. */
export interface BindMarker {
  readonly field: string;
  readonly optionsFrom: string;
  readonly kind?: string;
}

/** A distinct binding a contextual agent-level selector renders one control for. */
export interface BindDescriptor extends BindMarker {
  /** Stable map key: `optionsFrom` or `optionsFrom:kind`. */
  readonly key: string;
  readonly label: string;
}

/** Stable descriptor key shared with the backend merge (`optionsFrom[:kind]`). */
export function bindDescriptorKey(optionsFrom: string, kind?: string): string {
  return kind ? `${optionsFrom}:${kind}` : optionsFrom;
}

function readBindMarker(property: Record<string, unknown>): BindMarker | null {
  const marker = property['bindTimeConfigurableField'];
  if (typeof marker !== 'object' || marker === null) return null;
  const config = asRecord(marker);
  const field = stringValue(config['field']);
  const optionsFrom = stringValue(config['optionsFrom']);
  if (!field || !optionsFrom) return null;
  const kind = stringValue(config['kind']);
  return { field, optionsFrom, ...(kind ? { kind } : {}) };
}

function bindLabel(marker: BindMarker): string {
  if (marker.optionsFrom === BIND_SOURCE_CHANNEL_ACCOUNTS && marker.kind === 'calendar') return 'Agenda deste agente';
  if (marker.optionsFrom === 'business_hours_calendars') return 'Horário de funcionamento';
  return marker.field;
}

/**
 * Distinct binding descriptors across the given (enabled) tools — one contextual
 * agent-level control per descriptor, driven purely by the neutral tool metadata.
 */
export function bindDescriptorsFromTools(tools: readonly AgentToolDefinition[]): BindDescriptor[] {
  const byKey = new Map<string, BindDescriptor>();
  for (const tool of tools) {
    const properties = asRecord(asRecord(tool.inputSchema)['properties']);
    for (const rawProperty of Object.values(properties)) {
      const marker = readBindMarker(asRecord(rawProperty));
      if (!marker) continue;
      const key = bindDescriptorKey(marker.optionsFrom, marker.kind);
      if (!byKey.has(key)) byKey.set(key, { ...marker, key, label: bindLabel(marker) });
    }
  }
  return [...byKey.values()];
}

/** Picker options for a bind descriptor, from the relevant catalog. */
export function optionsForBindDescriptor(
  descriptor: BindMarker,
  catalogs: ToolFieldCatalogs,
): BuilderToolFieldOption[] {
  if (descriptor.optionsFrom === BIND_SOURCE_CHANNEL_ACCOUNTS) {
    const kind = descriptor.kind ?? '';
    return catalogs.channelAccounts
      .filter((account) => (kind ? String(account.kind) === kind : true))
      .filter((account): account is ChannelAccount & { externalChannelId: string } => Boolean(account.externalChannelId))
      .map((account) => ({
        value: account.externalChannelId,
        label: account.label || account.externalChannelId,
        description: account.kind,
      }));
  }
  if (descriptor.optionsFrom === BIND_SOURCE_BUSINESS_HOURS) {
    return catalogs.businessHoursCalendars.map((calendar) => ({ value: calendar.id, label: calendar.name }));
  }
  return [];
}

/**
 * Resolves picker options from a field's neutral `bindTimeConfigurableField`
 * marker. Returns null when the field carries no marker (so name heuristics run).
 */
function optionsFromBindMarker(
  property: Record<string, unknown>,
  catalogs: ToolFieldCatalogs,
): BuilderToolFieldOption[] | null {
  const marker = readBindMarker(property);
  if (!marker) return null;
  return optionsForBindDescriptor(marker, catalogs);
}

/** The agent-level bound value for a field, if the field is marked and the agent pinned one. */
function agentBindingValueForField(
  property: Record<string, unknown>,
  catalogs: ToolFieldCatalogs,
): string | null {
  const marker = readBindMarker(property);
  if (!marker) return null;
  return catalogs.agentSubAccountBindings[bindDescriptorKey(marker.optionsFrom, marker.kind)] ?? null;
}

function integrationToolPolicyDraft(tool: AgentToolDefinition, catalogs: ToolFieldCatalogs): BuilderToolPolicyDraft {
  const selectedIntegrationIds = new Set(catalogs.selectedIntegrationIds);
  const relevantIntegrations = catalogs.integrations.filter((integration) => {
    if (selectedIntegrationIds.size > 0 && !selectedIntegrationIds.has(integration.id)) return false;
    if (tool.integrationId) return integration.id === tool.integrationId;
    return Boolean(tool.providerKey) && integration.provider === tool.providerKey;
  });
  const selectedChannelAccountIds = new Set(catalogs.selectedChannelAccountIds);
  const selectedChannelAccounts = selectedChannelAccountIds.size > 0
    ? catalogs.channelAccounts.filter((account) => selectedChannelAccountIds.has(account.id))
    : [];

  return relevantIntegrations.reduce<BuilderToolPolicyDraft>((draft, integration) => {
    const metadata = asRecord(integration.metadata);
    const integrationDefaults = toolPolicyFromMetadataRecord(metadata['agentToolDefaults'], tool);
    const integrationProfiles = toolPolicyFromMetadataRecord(metadata['agentToolProfiles'], tool);
    const channelDefaults = channelToolPolicyFromMetadata(metadata['agentToolDefaultsByChannel'], tool, selectedChannelAccounts);
    const channelProfiles = channelToolPolicyFromMetadata(metadata['channelAccountToolDefaults'], tool, selectedChannelAccounts);
    return mergeToolPolicyDrafts(draft, integrationDefaults, integrationProfiles, channelDefaults, channelProfiles);
  }, emptyToolPolicyDraft());
}

function channelToolPolicyFromMetadata(
  value: unknown,
  tool: AgentToolDefinition,
  selectedChannelAccounts: readonly ChannelAccount[],
): BuilderToolPolicyDraft {
  const record = asRecord(value);
  return selectedChannelAccounts.reduce<BuilderToolPolicyDraft>((draft, account) => {
    if (tool.integrationId && account.integrationId !== tool.integrationId) return draft;
    return mergeToolPolicyDrafts(draft, toolPolicyFromMetadataRecord(record[account.id], tool));
  }, emptyToolPolicyDraft());
}

function toolPolicyFromMetadataRecord(value: unknown, tool: AgentToolDefinition): BuilderToolPolicyDraft {
  const record = asRecord(value);
  const keys = [tool.id, tool.key, tool.endpointKey].filter((key): key is string => Boolean(key));
  for (const key of keys) {
    if (record[key]) return normalizeToolPolicyDraft(record[key]);
  }
  return emptyToolPolicyDraft();
}

function mergeToolPolicyDrafts(
  base: BuilderToolPolicyDraft,
  ...drafts: readonly BuilderToolPolicyDraft[]
): BuilderToolPolicyDraft {
  return drafts.reduce<BuilderToolPolicyDraft>((merged, draft) => ({
    defaults: { ...merged.defaults, ...draft.defaults },
    fixedArguments: { ...merged.fixedArguments, ...draft.fixedArguments },
    fieldGuidance: { ...merged.fieldGuidance, ...draft.fieldGuidance },
    enabledOptionalFields: uniqueStrings([...merged.enabledOptionalFields, ...draft.enabledOptionalFields]),
    hiddenOptionalFields: uniqueStrings([...merged.hiddenOptionalFields, ...draft.hiddenOptionalFields]),
  }), base);
}

function uniqueStrings(values: readonly string[]): string[] {
  return [...new Set(values)];
}

function hasOwn(record: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function schemaEnumOptions(property: Record<string, unknown>): BuilderToolFieldOption[] {
  const directEnum = Array.isArray(property['enum']) ? property['enum'] : null;
  if (directEnum) {
    return directEnum.map((value) => ({
      value: String(value),
      label: String(value),
    }));
  }

  const items = asRecord(property['items']);
  const itemEnum = Array.isArray(items['enum']) ? items['enum'] : null;
  if (itemEnum) {
    return itemEnum.map((value) => ({
      value: String(value),
      label: String(value),
    }));
  }

  const composed = Array.isArray(property['oneOf'])
    ? property['oneOf']
    : Array.isArray(property['anyOf'])
      ? property['anyOf']
      : [];
  return composed.flatMap((item) => {
    const option = asRecord(item);
    const constant = option['const'];
    if (constant === undefined) return [];
    return [{
      value: String(constant),
      label: stringValue(option['title']) || String(constant),
      description: stringValue(option['description']),
    }];
  });
}

function readFieldValue(value: unknown, kind: BuilderToolFieldKind): BuilderToolFieldValue {
  if (value === null || value === undefined) {
    if (kind === 'multi_select') return [];
    if (kind === 'boolean') return false;
    return null;
  }
  if (kind === 'number') {
    const numeric = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }
  if (kind === 'boolean') return value === true || value === 'true';
  if (kind === 'multi_select') {
    return Array.isArray(value) ? value.map(String) : String(value).split(',').map((item) => item.trim()).filter(Boolean);
  }
  if (kind === 'json') return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return typeof value === 'string' ? value : String(value);
}

function hasMeaningfulValue(value: BuilderToolFieldValue): boolean {
  if (value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function schemaType(property: Record<string, unknown>): string {
  const type = property['type'];
  if (typeof type === 'string') return type;
  if (Array.isArray(type)) {
    return type.find((item): item is string => typeof item === 'string' && item !== 'null') ?? 'string';
  }
  return 'string';
}

function labelForField(key: string, property: Record<string, unknown>): string {
  return stringValue(property['title']) || key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toLocaleUpperCase('pt-BR'));
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function stringRecord(value: unknown): Record<string, string> {
  return Object.fromEntries(
    Object.entries(asRecord(value)).filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
  );
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}
